import type { CSSProperties } from "react";
import type { ScopeVariableColor } from "../types";

export type PluginTone =
    | "accent"
    | "error"
    | "info"
    | "muted"
    | "success"
    | "warning";
export type PluginToneSurface =
    | "text"
    | "surface"
    | "banner"
    | "chip"
    | "soft";
export type PluginConsoleSurface = "raised" | "surface";
export type OutputValueType =
    | "array"
    | "boolean"
    | "null"
    | "number"
    | "object"
    | "other"
    | "string";

const TONE_VARIABLES: Record<PluginTone, string> = {
    accent: "--color-primary",
    error: "--color-destructive",
    info: "--color-info",
    muted: "--color-muted-foreground",
    success: "--color-success",
    warning: "--color-warning",
};

const CONSOLE_VARIABLES = {
    border: "--editor-console-border",
    danger: "--editor-console-danger",
    foreground: "--editor-console-foreground",
    info: "--editor-console-info",
    mutedForeground: "--editor-console-muted-foreground",
    raised: "--editor-console-raised",
    selection: "--editor-selection-background",
    success: "--editor-console-success",
    surface: "--editor-console-surface",
    warning: "--editor-console-warning",
} as const;

function cssVariable(name: string): string {
    return `var(${name})`;
}

function readCssVariable(name: string, fallback: string): string {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return fallback;
    }

    const value = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
    return value || fallback;
}

function colorMix(
    baseColor: string,
    amount: number,
    fallback = "transparent",
): string {
    return `color-mix(in srgb, ${baseColor} ${amount}%, ${fallback})`;
}

export function getPluginToneValue(tone: PluginTone): string {
    return cssVariable(TONE_VARIABLES[tone]);
}

export function getPluginConsoleStyle(
    surface: PluginConsoleSurface = "surface",
): CSSProperties {
    return {
        backgroundColor: cssVariable(CONSOLE_VARIABLES[surface]),
        borderColor: cssVariable(CONSOLE_VARIABLES.border),
        color: cssVariable(CONSOLE_VARIABLES.foreground),
    };
}

export function getPluginTerminalTheme(): Record<string, string> {
    return {
        background: readCssVariable(CONSOLE_VARIABLES.surface, "#0f172a"),
        foreground: readCssVariable(CONSOLE_VARIABLES.foreground, "#e2e8f0"),
        cursor: readCssVariable(CONSOLE_VARIABLES.foreground, "#e2e8f0"),
        cursorAccent: readCssVariable(CONSOLE_VARIABLES.surface, "#0f172a"),
        selectionBackground: readCssVariable(CONSOLE_VARIABLES.selection, "#1d4ed8"),
        black: readCssVariable(CONSOLE_VARIABLES.border, "#334155"),
        red: readCssVariable("--color-destructive", "#fb7185"),
        green: readCssVariable("--color-success", "#4ade80"),
        yellow: readCssVariable("--color-warning", "#facc15"),
        blue: readCssVariable("--color-info", "#60a5fa"),
        magenta: readCssVariable("--color-primary", "#c084fc"),
        cyan: readCssVariable("--content-link", "#22d3ee"),
        white: readCssVariable(CONSOLE_VARIABLES.foreground, "#e2e8f0"),
        brightBlack: readCssVariable(CONSOLE_VARIABLES.mutedForeground, "#94a3b8"),
        brightRed: "#fda4af",
        brightGreen: "#86efac",
        brightYellow: "#fde047",
        brightBlue: "#93c5fd",
        brightMagenta: "#d8b4fe",
        brightCyan: "#67e8f9",
        brightWhite: "#f8fafc",
    };
}

export function getPluginToneStyle(
    tone: PluginTone,
    surface: PluginToneSurface = "text",
): CSSProperties {
    const color = getPluginToneValue(tone);

    switch (surface) {
        case "surface":
            return {
                backgroundColor: colorMix(color, 10),
                borderColor: colorMix(color, 22, cssVariable("--color-border")),
                color,
            };
        case "banner":
            return {
                backgroundColor: colorMix(color, 6),
                borderColor: colorMix(color, 18, cssVariable("--color-border")),
                color,
            };
        case "chip":
            return {
                backgroundColor: colorMix(color, 10),
                color,
            };
        case "soft":
            return {
                color: colorMix(color, 82, cssVariable("--color-foreground")),
            };
        default:
            return { color };
    }
}

export function getScopeVariableTone(color: ScopeVariableColor): PluginTone {
    switch (color) {
        case "amber":
            return "warning";
        case "blue":
            return "info";
        case "green":
            return "success";
        case "purple":
            return "accent";
        default:
            return "muted";
    }
}

export function getScopeVariableStyle(
    color: ScopeVariableColor,
    surface: PluginToneSurface = "text",
): CSSProperties {
    return getPluginToneStyle(getScopeVariableTone(color), surface);
}

export function getOutputValueTone(type: OutputValueType): PluginTone {
    switch (type) {
        case "boolean":
            return "warning";
        case "number":
            return "info";
        case "string":
            return "success";
        case "array":
        case "object":
            return "accent";
        default:
            return "muted";
    }
}

export function getOutputValueStyle(
    type: OutputValueType,
    surface: PluginToneSurface = "text",
): CSSProperties {
    return getPluginToneStyle(getOutputValueTone(type), surface);
}
